import type { UpstreamProtocol } from './internal-upstreams.ts';
import type { GatewayMutationReceipt } from './gateway-mutation-receipts.ts';

export type UpstreamCapabilityStatus =
  | 'passed'
  | 'needs_probe'
  | 'probe_failed'
  | 'missing_from_list'
  | 'mismatch'
  | 'ignored';

export interface UpstreamModelCapabilityRecord {
  readonly id: string;
  readonly upstream_id: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly channel_id: string;
  readonly channel_slug: string;
  readonly vendor: string;
  readonly model: string;
  readonly protocol: UpstreamProtocol;
  readonly upstream_model_name: string;
  readonly source_vendor: string | null;
  readonly source_model: string;
  readonly probe_protocol: UpstreamProtocol | null;
  readonly status: UpstreamCapabilityStatus;
  readonly last_probe_outcome: string | null;
  readonly last_probe_at: string | null;
  readonly last_passed_at: string | null;
  readonly upstream_status: number | null;
  readonly error: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListUpstreamCapabilitiesResponse {
  readonly capabilities: readonly UpstreamModelCapabilityRecord[];
}

/**
 * Maximum number of model ids one discovery call may carry — both the ids a
 * caller submits in `models` and the entries the gateway will accept back
 * from an upstream's own list response.
 *
 * Lives in the contract for the same reason as `MAX_SWITCH_REASON_LENGTH`
 * (`internal-tenant-routing.ts`): the two sides fail asymmetrically. A
 * console limit ABOVE the gateway's over-sends and surfaces the gateway's
 * `INVALID_PAYLOAD` — noisy but safe. A console limit BELOW the gateway's
 * silently splits a paste into more batches than needed, and the operator
 * never learns the real cap. Two literals held together by a comment drift;
 * one exported constant cannot.
 */
export const MAX_DISCOVERY_MODEL_LIST_ITEMS = 200;

/**
 * Maximum length of a single discovery model id. Shared for the same reason
 * as {@link MAX_DISCOVERY_MODEL_LIST_ITEMS}: the console rejects a too-long
 * id before the round trip, and that pre-check must agree exactly with the
 * gateway validation that owns the refusal.
 */
export const MAX_DISCOVERY_MODEL_ID_LENGTH = 512;

export interface DiscoverUpstreamCapabilitiesRequest {
  readonly protocol?: UpstreamProtocol;
  readonly account_id?: string;
  readonly key_bucket_id?: string;
  readonly channel_id?: string;
  readonly channel_slugs_by_id?: Readonly<Record<string, string>>;
  readonly channel_vendors_by_id?: Readonly<Record<string, string>>;
  readonly channel_protocols_by_id?: Readonly<Record<string, UpstreamProtocol>>;
  readonly models?: readonly string[];
  readonly limit?: number;
}

export interface DiscoverUpstreamCapabilitiesResponse {
  readonly upstream_id: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly channel_id: string;
  readonly channel_slug: string;
  readonly capabilities: readonly UpstreamModelCapabilityRecord[];
  readonly routing_revision: string;
  readonly mutation_receipt: GatewayMutationReceipt;
}

export interface SetUpstreamCapabilityProbeProtocolRequest {
  readonly probe_protocol: UpstreamProtocol;
}

export type BatchUpstreamCapabilityAction =
  | 'probe'
  | 'set_probe_protocol'
  | 'ignore'
  | 'unignore';

/**
 * One exact physical capability result. Mutation responses deliberately use
 * the same normalized shape for single and batch actions so downstream audit
 * code never has to reconstruct action identity from the request body.
 */
export interface UpstreamCapabilityActionResult {
  readonly capability_id: string;
  readonly queued: boolean;
  readonly capability: UpstreamModelCapabilityRecord;
}

export interface UpstreamCapabilityActionResponse<
  TAction extends BatchUpstreamCapabilityAction = BatchUpstreamCapabilityAction,
> {
  readonly upstream_id: string;
  readonly action: TAction;
  /** Trimmed, de-duplicated exact capability ids, in first-seen order. */
  readonly capability_ids: readonly string[];
  /** One result per normalized id, in the same order as `capability_ids`. */
  readonly results: readonly UpstreamCapabilityActionResult[];
  /** Decimal string backed by upstream_configs.routing_revision (BIGINT). */
  readonly routing_revision: string;
  readonly queued_count: number;
  readonly mutation_receipt?: import('./gateway-mutation-receipts.ts').GatewayMutationReceipt;
}

export type ProbeUpstreamCapabilityResponse = UpstreamCapabilityActionResponse<'probe'>;

export type UpdateUpstreamCapabilityResponse = UpstreamCapabilityActionResponse<
  'set_probe_protocol' | 'ignore' | 'unignore'
>;

export interface BatchUpstreamCapabilitiesRequest {
  readonly action: BatchUpstreamCapabilityAction;
  readonly capability_ids: readonly string[];
  readonly probe_protocol?: UpstreamProtocol;
}

export type BatchUpstreamCapabilitiesResponse = UpstreamCapabilityActionResponse;

export type UpstreamCapabilitySkuTargetScope =
  | 'default'
  | 'config'
  | 'org'
  | 'org_override';

export interface UpstreamSkuRoutingScopeCounts {
  readonly default: number;
  readonly config: number;
  readonly org: number;
  readonly org_override: number;
}

/**
 * Enabled SKU target that would lose its final live canonical capability if
 * the requested exact capability ids were ignored (#1157).
 */
export interface UpstreamCapabilitySkuTargetImpact {
  readonly scope: UpstreamCapabilitySkuTargetScope;
  readonly target_id: string;
  readonly sku_id: string;
  readonly config_id: string | null;
  readonly org_id: string | null;
  readonly capability_id: string;
}

export interface UpstreamCapabilityMutationImpact {
  /** The exact action identity; physical-model siblings are never implied. */
  readonly capability_ids: readonly string[];
  /** Bounded examples; use `totals` for safety decisions and operator counts. */
  readonly sku_targets: readonly UpstreamCapabilitySkuTargetImpact[];
  readonly totals: {
    readonly sku_targets: number;
    readonly scope_counts: UpstreamSkuRoutingScopeCounts;
  };
  readonly truncated: {
    readonly sku_targets: boolean;
  };
}

export interface PreviewUpstreamCapabilityMutationRequest {
  readonly action: 'ignore' | 'unignore';
  /** Exact physical capability ids; canonical siblings are never implied. */
  readonly capability_ids: readonly string[];
}

export interface PreviewUpstreamCapabilityMutationResponse {
  readonly upstream_id: string;
  readonly action: PreviewUpstreamCapabilityMutationRequest['action'];
  readonly capabilities: readonly UpstreamModelCapabilityRecord[];
  readonly protected: boolean;
  readonly impact: UpstreamCapabilityMutationImpact;
}

/**
 * Structured 409 returned before an exact ignore would remove live routing
 * coverage. The same body is used by single and batch actions.
 */
export interface UpstreamCapabilityInUseErrorBody {
  readonly error: {
    readonly code: 'UPSTREAM_CAPABILITY_IN_USE';
    readonly message: string;
    readonly retryable: false;
    readonly capability_impact: UpstreamCapabilityMutationImpact;
  };
}

/**
 * One SKU + exact supply identity summary for the upstream operator screen.
 * `default_target` is editable; the other scopes are intentionally counts-only.
 */
export interface UpstreamSkuRoutingIdentitySummary {
  readonly sku_id: string;
  readonly sku_name: string;
  readonly vendor: string;
  readonly model: string;
  readonly capability_id: string | null;
  readonly upstream_id: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly upstream_channel_id: string;
  readonly upstream_channel_slug: string;
  readonly default_target: import('./internal-tenant-routing.ts').RoutingSkuTargetRecord | null;
  readonly scope_counts: UpstreamSkuRoutingScopeCounts;
}

/**
 * Exact physical supply identity accepted by the paginated routing summary.
 * All fields are required together so a drawer cannot accidentally hydrate
 * from a broader or stale result set.
 */
export interface UpstreamSkuRoutingIdentityFilter {
  readonly capability_id: string;
  readonly vendor: string;
  readonly model: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly upstream_channel_id: string;
}

export interface UpstreamSkuRoutingSummaryPagination {
  readonly limit: number;
  readonly offset: number;
  readonly returned: number;
  readonly total: number;
  readonly has_more: boolean;
}

export interface GetUpstreamSkuRoutingSummaryResponse {
  readonly upstream_id: string;
  /** Decimal string backed by upstream_configs.routing_revision (BIGINT). */
  readonly routing_revision: string;
  /** Exact SKU narrowing applied by the server, or null for all SKUs. */
  readonly sku_id_filter: string | null;
  /** Null for the bounded overview; exact drawer reads echo their full filter. */
  readonly identity_filter: UpstreamSkuRoutingIdentityFilter | null;
  readonly service_sku_count: number;
  readonly scope_counts: UpstreamSkuRoutingScopeCounts;
  readonly identities: readonly UpstreamSkuRoutingIdentitySummary[];
  readonly pagination: UpstreamSkuRoutingSummaryPagination;
}
